"use client";

import { c as _c } from "react/compiler-runtime";
import { FallbackImage } from "@/components/common/FallbackImage";
import { useDropImageGallery } from "@/components/drops/view/part/DropImageGalleryProvider";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useInView } from "@/hooks/useInView";
import React, { useCallback, useRef, useState } from "react";
import { InlineMediaActions } from "./MediaActionToolbar";
import { ImageMediaModal, requestCenteredImageFullscreen } from "./ImageMediaModal";
import type { MediaLoadStrategy } from "./mediaLoadStrategy";
import { useMediaActions } from "./useMediaActions";
const loadingPlaceholderStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  maxWidth: "100%",
  maxHeight: "100%",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)"
};
function LoadingPlaceholder(t0) {
  const $ = _c(2);
  const {
    hasTouchScreen
  } = t0;
  const t1 = `tw-rounded-xl tw-bg-iron-800 ${hasTouchScreen ? "" : "tw-animate-pulse"}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className={t1} style={loadingPlaceholderStyle} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function ImageButton(t0) {
  const $ = _c(4);
  const {
    children,
    intrinsicHeight,
    onClick
  } = t0;
  const t1 = `tw-border-0 tw-bg-transparent tw-p-0 ${intrinsicHeight ? "tw-block tw-w-full" : "tw-absolute tw-inset-0 tw-h-full tw-w-full"}`;
  let t2;
  if ($[0] !== children || $[1] !== onClick || $[2] !== t1) {
    t2 = <button type="button" className={t1} onClick={onClick} aria-label="Open image preview">{children}</button>;
    $[0] = children;
    $[1] = onClick;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function RetryImageMessage(t0) {
  const $ = _c(3);
  const {
    onRetry
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="tw-text-sm tw-text-iron-400">Couldn’t load image.</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onRetry) {
    t2 = <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2">{t1}<button onClick={onRetry} className="tw-rounded-md tw-bg-iron-700 tw-px-3 tw-py-1 tw-text-xs tw-text-white hover:tw-bg-iron-600">Retry</button></div>;
    $[1] = onRetry;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function DropImageContent(t0) {
  const $ = _c(17);
  const {
    src,
    primarySrc,
    currentSrc,
    retryTick,
    imgRef,
    loaded,
    disableModal,
    intrinsicHeight,
    loadStrategy,
    resolvedObjectPosition,
    handleImageLoad,
    handleImageClick,
    handleIntrinsicImageError,
    handleError
  } = t0;
  const imageClassName = `${intrinsicHeight ? "tw-max-w-full" : "tw-max-h-full tw-max-w-full"} ${loaded ? "tw-opacity-100" : "tw-opacity-0"} ${disableModal ? "" : "tw-cursor-pointer"}`;
  let t1;
  if ($[0] !== currentSrc || $[1] !== handleError || $[2] !== handleImageLoad || $[3] !== handleIntrinsicImageError || $[4] !== imageClassName || $[5] !== imgRef || $[6] !== intrinsicHeight || $[7] !== loadStrategy || $[8] !== primarySrc || $[9] !== resolvedObjectPosition || $[10] !== retryTick || $[11] !== src) {
    t1 = intrinsicHeight ? <img key={retryTick} ref={imgRef} src={currentSrc} alt="Drop media" loading={loadStrategy === "eager" ? "eager" : "lazy"} className={`tw-block tw-h-auto tw-max-h-64 tw-w-full tw-object-contain ${imageClassName}`} style={{
      objectPosition: resolvedObjectPosition
    }} onLoad={handleImageLoad} onError={handleIntrinsicImageError} /> : <FallbackImage key={retryTick} ref={imgRef} primarySrc={primarySrc} fallbackSrc={src} alt="Drop media" optimize={false} fill={true} loading={loadStrategy === "eager" ? "eager" : undefined} sizes="(max-width: 768px) 100vw, 768px" className={imageClassName} style={{
      objectFit: "contain",
      objectPosition: resolvedObjectPosition
    }} onLoad={handleImageLoad} onError={handleError} />;
    $[0] = currentSrc;
    $[1] = handleError;
    $[2] = handleImageLoad;
    $[3] = handleIntrinsicImageError;
    $[4] = imageClassName;
    $[5] = imgRef;
    $[6] = intrinsicHeight;
    $[7] = loadStrategy;
    $[8] = primarySrc;
    $[9] = resolvedObjectPosition;
    $[10] = retryTick;
    $[11] = src;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const image = t1;
  if (disableModal) {
    return image;
  }
  let t2;
  if ($[13] !== handleImageClick || $[14] !== image || $[15] !== intrinsicHeight) {
    t2 = <ImageButton intrinsicHeight={intrinsicHeight} onClick={handleImageClick}>{image}</ImageButton>;
    $[13] = handleImageClick;
    $[14] = image;
    $[15] = intrinsicHeight;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
}
function DropListItemContentMediaImage(t0) {
  const $ = _c(77);
  const {
    src,
    maxRetries: t1,
    isCompetitionDrop: t2,
    disableModal: t3,
    imageObjectPosition,
    imageScale: t4,
    loadStrategy: t5,
    intrinsicHeight: t6,
    galleryItemId
  } = t0;
  const maxRetries = t1 === undefined ? 0 : t1;
  const isCompetitionDrop = t2 === undefined ? false : t2;
  const disableModal = t3 === undefined ? false : t3;
  const imageScale = t4 === undefined ? ImageScale.AUTOx450 : t4;
  const loadStrategy = t5 === undefined ? "in-view" : t5;
  const intrinsicHeight = t6 === undefined ? false : t6;
  const [ref, inView] = useInView();
  const [loaded, setLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const {
    isCapacitor
  } = useCapacitor();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const imageGallery = useDropImageGallery();
  const imgRef = useRef(null);
  const modalImageRef = useRef(null);
  let t7;
  if ($[0] !== src) {
    t7 = {
      url: src,
      fallbackFileName: "image",
      dialogTitle: "Save image",
      mimeType: "image"
    };
    $[0] = src;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const {
    downloadMedia,
    isDownloading,
    openLabel,
    openMedia
  } = useMediaActions(t7);
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setLoaded(true);
    };
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const handleImageLoad = t8;
  let t9;
  if ($[3] !== errorCount || $[4] !== maxRetries) {
    t9 = () => {
      if (errorCount >= maxRetries) {
        return;
      }
      const delay = 500 * 2 ** errorCount;
      setTimeout(() => {
        setErrorCount(_temp);
        setRetryTick(_temp2);
      }, delay);
    };
    $[3] = errorCount;
    $[4] = maxRetries;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const handleError = t9;
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setErrorCount(0);
      setLoaded(false);
      setRetryTick(_temp3);
    };
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const manualRetry = t10;
  let t11;
  if ($[7] !== disableModal || $[8] !== galleryItemId || $[9] !== imageGallery) {
    t11 = () => {
      if (disableModal) {
        return;
      }
      if (galleryItemId && imageGallery?.openImage(galleryItemId)) {
        return;
      }
      setIsModalOpen(true);
    };
    $[7] = disableModal;
    $[8] = galleryItemId;
    $[9] = imageGallery;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const openModal = t11;
  let t12;
  if ($[11] !== disableModal || $[12] !== openModal) {
    t12 = event => {
      if (disableModal) {
        return;
      }
      event.stopPropagation();
      openModal();
    };
    $[11] = disableModal;
    $[12] = openModal;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const handleImageClick = t12;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = event_0 => {
      event_0?.stopPropagation();
      setIsModalOpen(false);
    };
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const handleCloseModal = t13;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      const fullscreenTarget = modalImageRef.current ?? imgRef.current;
      if (fullscreenTarget) {
        requestCenteredImageFullscreen(fullscreenTarget);
      }
    };
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const handleFullScreen = t14;
  const shouldLoadImage = loadStrategy === "eager" || inView;
  const resolvedObjectPosition = imageObjectPosition ?? (isCompetitionDrop ? "center" : "left top");
  let t15;
  if ($[16] !== imageScale || $[17] !== src) {
    t15 = getScaledImageUri(src, imageScale);
    $[16] = imageScale;
    $[17] = src;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const primarySrc = t15;
  const [currentSrc, setCurrentSrc] = useState(primarySrc);
  const [usedFallback, setUsedFallback] = useState(false);
  let t16;
  let t17;
  if ($[19] !== primarySrc) {
    t16 = () => {
      setCurrentSrc(primarySrc);
      setUsedFallback(false);
    };
    t17 = [primarySrc];
    $[19] = primarySrc;
    $[20] = t16;
    $[21] = t17;
  } else {
    t16 = $[20];
    t17 = $[21];
  }
  React.useEffect(t16, t17);
  let t18;
  if ($[22] !== currentSrc || $[23] !== handleError || $[24] !== src || $[25] !== usedFallback) {
    t18 = () => {
      if (!usedFallback) {
        if (currentSrc !== src) {
          setCurrentSrc(src);
          setUsedFallback(true);
          return;
        }
      }
      handleError();
    };
    $[22] = currentSrc;
    $[23] = handleError;
    $[24] = src;
    $[25] = usedFallback;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const handleIntrinsicImageError = t18;
  const t19 = `tw-relative tw-flex tw-w-full tw-items-center ${intrinsicHeight ? "tw-min-h-40" : "tw-h-full"} ${isCompetitionDrop ? "tw-justify-center" : ""}`;
  let t20;
  if ($[27] !== disableModal || $[28] !== downloadMedia || $[29] !== isCapacitor || $[30] !== isDownloading || $[31] !== openLabel || $[32] !== openMedia) {
    t20 = !disableModal && <InlineMediaActions variant="image" onOpen={openMedia} openLabel={openLabel} onDownload={downloadMedia} isDownloading={isDownloading} onFullscreen={handleFullScreen} fullscreenTargetAvailable={!isCapacitor} />;
    $[27] = disableModal;
    $[28] = downloadMedia;
    $[29] = isCapacitor;
    $[30] = isDownloading;
    $[31] = openLabel;
    $[32] = openMedia;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== errorCount || $[35] !== hasTouchScreen || $[36] !== loaded || $[37] !== maxRetries) {
    t21 = !loaded && errorCount <= maxRetries && <LoadingPlaceholder hasTouchScreen={hasTouchScreen} />;
    $[34] = errorCount;
    $[35] = hasTouchScreen;
    $[36] = loaded;
    $[37] = maxRetries;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== currentSrc || $[40] !== disableModal || $[41] !== errorCount || $[42] !== handleError || $[43] !== handleImageClick || $[44] !== handleIntrinsicImageError || $[45] !== intrinsicHeight || $[46] !== loadStrategy || $[47] !== loaded || $[48] !== maxRetries || $[49] !== primarySrc || $[50] !== resolvedObjectPosition || $[51] !== retryTick || $[52] !== shouldLoadImage || $[53] !== src) {
    t22 = shouldLoadImage && errorCount <= maxRetries && <DropImageContent src={src} primarySrc={primarySrc} currentSrc={currentSrc} retryTick={retryTick} imgRef={imgRef} loaded={loaded} disableModal={disableModal} intrinsicHeight={intrinsicHeight} loadStrategy={loadStrategy} resolvedObjectPosition={resolvedObjectPosition} handleImageLoad={handleImageLoad} handleImageClick={handleImageClick} handleIntrinsicImageError={handleIntrinsicImageError} handleError={handleError} />;
    $[39] = currentSrc;
    $[40] = disableModal;
    $[41] = errorCount;
    $[42] = handleError;
    $[43] = handleImageClick;
    $[44] = handleIntrinsicImageError;
    $[45] = intrinsicHeight;
    $[46] = loadStrategy;
    $[47] = loaded;
    $[48] = maxRetries;
    $[49] = primarySrc;
    $[50] = resolvedObjectPosition;
    $[51] = retryTick;
    $[52] = shouldLoadImage;
    $[53] = src;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== errorCount || $[56] !== maxRetries) {
    t23 = errorCount > maxRetries && <RetryImageMessage onRetry={manualRetry} />;
    $[55] = errorCount;
    $[56] = maxRetries;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== ref || $[59] !== t19 || $[60] !== t20 || $[61] !== t21 || $[62] !== t22 || $[63] !== t23) {
    t24 = <div ref={ref} className={t19}>{t20}{t21}{t22}{t23}</div>;
    $[58] = ref;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== disableModal || $[66] !== downloadMedia || $[67] !== isCapacitor || $[68] !== isDownloading || $[69] !== isModalOpen || $[70] !== openLabel || $[71] !== openMedia || $[72] !== src) {
    t25 = !disableModal && isModalOpen && <ImageMediaModal src={src} imageRef={modalImageRef} onClose={() => handleCloseModal()} onOpen={openMedia} openLabel={openLabel} onDownload={downloadMedia} isDownloading={isDownloading} onFullscreen={handleFullScreen} fullscreenTargetAvailable={!isCapacitor} />;
    $[65] = disableModal;
    $[66] = downloadMedia;
    $[67] = isCapacitor;
    $[68] = isDownloading;
    $[69] = isModalOpen;
    $[70] = openLabel;
    $[71] = openMedia;
    $[72] = src;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== t24 || $[75] !== t25) {
    t26 = <>{t24}{t25}</>;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  return t26;
}
function _temp3(t_0) {
  return t_0 + 1;
}
function _temp2(t) {
  return t + 1;
}
function _temp(n) {
  return n + 1;
}
export default React.memo(DropListItemContentMediaImage);